import ContextFindDialog from './navigation/ContextFindDialog'
import { useContextualNavigation } from './navigation/useRead'
import DocumentLink from './documents/DocumentLink'
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { apiFetch } from '../lib/api'
import type { RecordTablePayload } from './RecordTable'
import { fetchDocuments, type DocRow } from './documentsList'
import { noteFundMenu, rememberFundChoice } from './fundScope'
import './find.css'

type Fund = { id: string; name: string }
type Instrument = { id: string; name: string; issuer: string | null; funds: Fund[] }
type SearchData = {
  funds: Fund[]
  instruments: Instrument[]
  failedFunds: Fund[]
  complete: boolean
}

/** Loads only records accessible through the existing fund list, three at a time.
 * Partial results stay usable, while failures remain explicit and retryable. */
export default function FindDialog({ onClose }: { onClose: () => void }) {
  const capability = useContextualNavigation()
  if (capability.loading) return null
  if (capability.error) return <ContextFindDialog onClose={onClose} error={capability.error} />
  return capability.value?.enabled ? (
    <ContextFindDialog onClose={onClose} />
  ) : (
    <LegacyFindDialog onClose={onClose} />
  )
}
function LegacyFindDialog({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate()
  const dialog = useRef<HTMLDialogElement>(null)
  const input = useRef<HTMLInputElement>(null)
  const [query, setQuery] = useState('')
  const [retry, setRetry] = useState(0)
  const [data, setData] = useState<SearchData | null>(null)
  const [fundError, setFundError] = useState(false)
  const [docs, setDocs] = useState<DocRow[] | null>(null)
  const [docError, setDocError] = useState(false)
  useEffect(() => {
    const previous = document.activeElement
    const element = dialog.current
    element?.showModal()
    input.current?.focus()
    noteFundMenu(1)
    return () => {
      noteFundMenu(-1)
      element?.close()
      if (previous instanceof HTMLElement && previous.isConnected) previous.focus()
    }
  }, [])
  useEffect(() => {
    const controller = new AbortController()
    const signal = controller.signal
    setData(null)
    setDocs(null)
    setFundError(false)
    setDocError(false)
    fetchDocuments({ signal })
      .then((rows) => {
        if (!signal.aborted) setDocs(rows)
      })
      .catch(() => {
        if (!signal.aborted) setDocError(true)
      })
    void (async () => {
      try {
        const funds = await apiFetch<Fund[]>('/api/funds', { signal })
        if (signal.aborted) return
        const instruments = new Map<string, Instrument>()
        const failedFunds: Fund[] = []
        let cursor = 0
        const publish = (complete = false) => {
          if (!signal.aborted)
            setData({
              funds,
              instruments: [...instruments.values()].map((i) => ({ ...i, funds: [...i.funds] })),
              failedFunds: [...failedFunds],
              complete,
            })
        }
        publish()
        await Promise.all(
          Array.from({ length: Math.min(3, funds.length) }, async () => {
            while (!signal.aborted && cursor < funds.length) {
              const fund = funds[cursor++]
              try {
                const record = await apiFetch<RecordTablePayload>(
                  `/api/funds/${encodeURIComponent(fund.id)}/record-table?state=recorded&include_proposed=false`,
                  { signal }
                )
                if (signal.aborted) return
                for (const row of record.instruments) {
                  if (row.state === 'proposed') continue
                  const item: Instrument = instruments.get(row.id) ?? {
                    id: row.id,
                    name: row.name,
                    issuer: row.issuer_name ?? null,
                    funds: [],
                  }
                  if (!item.funds.some((f) => f.id === fund.id)) item.funds.push(fund)
                  instruments.set(row.id, item)
                }
              } catch {
                if (signal.aborted) return
                failedFunds.push(fund)
              }
              publish()
            }
          })
        )
        publish(true)
      } catch {
        if (!signal.aborted) setFundError(true)
      }
    })()
    return () => controller.abort()
  }, [retry])
  const q = query.trim().toLocaleLowerCase()
  const funds = (data?.funds ?? []).filter((f) => f.name.toLocaleLowerCase().includes(q))
  const instruments = (data?.instruments ?? [])
    .filter((i) => i.name.toLocaleLowerCase().includes(q))
    .sort((a, b) => a.name.localeCompare(b.name))
  const documents = (docs ?? []).filter((d) =>
    (d.display_title ?? d.filename).toLocaleLowerCase().includes(q)
  )
  const go = (path: string, fundId?: string) => {
    if (fundId) rememberFundChoice(fundId)
    onClose()
    navigate(path)
  }
  const retryButton = (
    <button type="button" onClick={() => setRetry((v) => v + 1)}>
      Retry search
    </button>
  )
  return (
    <dialog
      ref={dialog}
      className="find-dialog"
      aria-labelledby="find-title"
      onCancel={(e) => {
        e.preventDefault()
        onClose()
      }}
      onKeyDown={(e) => {
        // The modal owns keys; keep fund selector shortcuts and queue handlers outside it.
        e.stopPropagation()
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          e.preventDefault()
          const choices = Array.from(
            dialog.current?.querySelectorAll<HTMLButtonElement>('[data-find-result]') ?? []
          )
          const i = choices.indexOf(document.activeElement as HTMLButtonElement)
          const next =
            i < 0
              ? e.key === 'ArrowDown'
                ? 0
                : choices.length - 1
              : Math.max(0, Math.min(choices.length - 1, i + (e.key === 'ArrowDown' ? 1 : -1)))
          choices[next]?.focus()
        }
        if (e.key === 'Enter' && e.target === input.current) {
          e.preventDefault()
          dialog.current?.querySelector<HTMLButtonElement>('[data-find-result]')?.click()
        }
      }}
    >
      <header>
        <h2 id="find-title">Find</h2>
        <button type="button" onClick={onClose} aria-label="Close Find">
          Close ×
        </button>
      </header>
      <p>Find a fund, instrument or document by name.</p>
      <input
        ref={input}
        aria-label="Find by name"
        value={query}
        placeholder="Type a name…"
        onChange={(e) => setQuery(e.target.value)}
      />
      <div className="find-results">
        {!q ? (
          <p>Enter a name to see matching records. PDF contents are not searched.</p>
        ) : (
          <>
            <section aria-label="Matching funds">
              <h3>Funds</h3>
              {fundError ? (
                <p role="status">Funds could not be loaded. {retryButton}</p>
              ) : !data ? (
                <p role="status">Loading funds…</p>
              ) : funds.length === 0 ? (
                <p>No matching funds.</p>
              ) : (
                funds.map((f) => (
                  <button
                    key={f.id}
                    data-find-result
                    type="button"
                    onClick={() => go(`/v2/funds/${encodeURIComponent(f.id)}`, f.id)}
                  >
                    {f.name} <span>Open fund ›</span>
                  </button>
                ))
              )}
            </section>
            <section aria-label="Matching instruments">
              <h3>Instruments</h3>
              {fundError ? (
                <p role="status">Instrument search is unavailable. {retryButton}</p>
              ) : (
                <>
                  {!data?.complete && (
                    <p role="status">Loading instruments… Results may be incomplete.</p>
                  )}
                  {!!data?.failedFunds.length && (
                    <p role="status">
                      Could not search {data.failedFunds.map((f) => f.name).join(', ')}. Results are
                      incomplete. {retryButton}
                    </p>
                  )}
                  {data?.complete && !data.failedFunds.length && instruments.length === 0 && (
                    <p>No matching instruments.</p>
                  )}
                  {instruments.map((i) => (
                    <div key={i.id} className="find-instrument" data-find-instrument={i.id}>
                      <strong>{i.name}</strong>
                      <small className="find-issuer">
                        {i.issuer ? `Issued by ${i.issuer}` : 'Issuer not recorded'}
                      </small>
                      {i.funds.map((f) => (
                        <button
                          key={f.id}
                          data-find-result
                          type="button"
                          onClick={() =>
                            go(
                              `/v2/funds/${encodeURIComponent(f.id)}?tab=record&focus=${encodeURIComponent(`instrument:${i.id}`)}`,
                              f.id
                            )
                          }
                        >
                          {f.name} <span>Open instrument ›</span>
                        </button>
                      ))}
                    </div>
                  ))}
                </>
              )}
            </section>
            <section aria-label="Matching documents">
              <h3>Documents</h3>
              {docError ? (
                <p role="status">Documents could not be loaded. {retryButton}</p>
              ) : !docs ? (
                <p role="status">Loading documents…</p>
              ) : documents.length === 0 ? (
                <p>No matching documents.</p>
              ) : (
                documents.map((d) => (
                  <div key={d.id}>
                    <DocumentLink
                      data-find-result
                      document={{ document_id: d.id, display_title: d.display_title }}
                    />
                    <small>{d.fund_names?.join(' · ') || 'No matched fund'}</small>
                  </div>
                ))
              )}
            </section>
          </>
        )}
      </div>
    </dialog>
  )
}
